// AiGeneratePanel — the AI tab content inside <DesignStep>.
//
// v3 (June 2026): added the Creative Direction filter panel. 5 controls
// in two zones:
//   Vibe zone:  Vibe · Light · Framing
//   Brand zone: Brand presence · Show logo
// Plus a "Surprise me" button that re-randomizes non-logo values
// (zero API cost — it just touches state).
//
// State for the filter selections is OWNED BY THE PARENT (/appv2/page.tsx)
// so it survives back-nav and so the parent can read it for telemetry /
// regen scenarios. Panel only reads + writes via callback.
//
// API call lifecycle is still owned here, but the start/result/error
// events lift to the parent so the wizard can auto-advance to the
// terminal step the moment Generate is clicked.

import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCredits } from '@/hooks/useCredits';
import {
  wizardAiGenerate,
  InsufficientCreditsError,
  type CreativeBackground,
  type CreativeBrandPresence,
  type CreativeDirection,
  type CreativeFraming,
  type CreativeLight,
  type CreativeTextDensity,
  type CreativeVibe,
  type Quality,
  type WizardAiGenerateResult,
} from '@/lib/api';

interface AiGeneratePanelProps {
  /** Wizard route — controls frame count + which backend pipeline fires. */
  route: 'single' | 'carousel' | 'product-shot' | 'social-post';
  /** Product image from the wizard's Upload step. */
  productFile: File | null;
  productName: string;
  productDescription: string;
  /** Aspect ratio from the Platform step. Social-post is always 1:1. */
  aspectRatio: string;
  /** Quality from the Quality step. Social-post forces 'low'. */
  quality: Quality;
  /** Filter selections — owned by the parent so they survive navigation. */
  creativeDirection: CreativeDirection;
  onCreativeDirectionChange: (cd: CreativeDirection) => void;
  /** Fired the moment Generate is clicked (before the API call). Parent
   *  uses this to auto-advance to the terminal step so the loading
   *  skeleton appears there immediately. */
  onGenerateStart: () => void;
  /** Fires when Generate succeeds. Parent lifts the result up and the
   *  terminal step renders the AI viewer. */
  onResult: (result: WizardAiGenerateResult) => void;
  /** Fires on failure. Parent surfaces the error (e.g. back to design
   *  step with a banner). */
  onError: (message: string) => void;
}


// ---------- Filter value tables ----------
// Keep in sync with backend/main.py's _CD_* maps. New values added here
// without a matching backend entry silently drop (no error — but they
// also don't influence the generation).
// Exported so other AI tab surfaces (e.g. ProductShotAiExplainer in
// /appv2/page.tsx) can render the same filter UI without duplication.

export const VIBE_VALUES: readonly CreativeVibe[] = [
  'Premium', 'Warm & lifestyle', 'Playful', 'Clean & minimal', 'Editorial',
];
export const LIGHT_VALUES: readonly CreativeLight[] = [
  'Soft daylight', 'Golden hour', 'Studio bright', 'Moody low-key',
];
export const FRAMING_VALUES: readonly CreativeFraming[] = [
  'Hero close-up', 'Centered product', 'Lifestyle wide', 'Flat lay top-down',
];
export const BACKGROUND_VALUES: readonly CreativeBackground[] = [
  'Studio', 'Lifestyle', 'Surface', 'Outdoors', 'Abstract',
];
export const BRAND_PRESENCE_VALUES: readonly CreativeBrandPresence[] = [
  'Subtle', 'Balanced', 'Bold',
];
export const TEXT_DENSITY_VALUES: readonly CreativeTextDensity[] = [
  'None', 'Text only', 'Text + CTA',
];


/** Sample image path for a given filter value. Returns null when no
 *  asset exists yet — the tooltip then renders text-only.
 *  Author samples once: /public/filter-previews/<value-slug>.webp.
 *  The slug uses kebab-case with no special chars (matches the
 *  backend/scripts/render_filter_previews.py output). */
function previewPathFor(value: string): string | null {
  const slug = value
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/\+/g, 'plus')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  // Returning the URL unconditionally; the <img onError> handler in
  // FilterPillTooltip drops the image area gracefully if the file
  // doesn't exist yet, so we don't need a hardcoded "is authored"
  // list here.
  return `/filter-previews/${slug}.webp`;
}


// ---------- CreativeDirectionFilters — shared filter UI ----------
// The 5-filter zones, reusable across AI tab surfaces. AiGeneratePanel
// wraps this + brand callout + Generate button; ProductShotAiExplainer
// wraps this + a "next: pick quality" hint card.

export interface CreativeDirectionFiltersProps {
  creativeDirection: CreativeDirection;
  onCreativeDirectionChange: (cd: CreativeDirection) => void;
  /** When true, renders the Text density chip row (Product Shot route).
   *  Default false — other routes don't surface this filter; the backend
   *  treats missing/undefined as the default (Text + CTA, today's
   *  behavior) so omission is safe. */
  showTextDensity?: boolean;
  /** When true, each FilterRow renders a small primary-tinted icon
   *  next to its label so the rows visually match the AspectRow /
   *  QualityRow rows beneath them (which have icons inline). Used by
   *  the /appv3 prototype. Default false — /appv2 keeps the icon-free
   *  labels it shipped with. */
  withIcons?: boolean;
  /** Optional override for the Framing row's values. Each /appv3 route
   *  has its own framing vocabulary — Product Shot uses composition
   *  framings (Hero close-up / Centered product / Lifestyle wide /
   *  Flat lay top-down); Held Product uses pose framings (Close-up
   *  grip / Two-hand present / Palm display / Mid-application / Hands
   *  only / Over-the-shoulder); Worn Item uses garment-showcase
   *  framings (Full-body editorial / Mid-shot lifestyle / Detail
   *  close-crop / Walking in-motion / Side profile / Styled layered).
   *  When omitted, the default Product Shot FRAMING_VALUES are used. */
  framingValues?: readonly CreativeFraming[];
}

// Icon map for withIcons mode. Keys match each FilterRow's label so the
// mapping is co-located and obvious. Icons are Remix Icon line variants
// to match AspectRow's ri-aspect-ratio-line + QualityRow's ri-star-line.
const _FILTER_ICONS: Record<string, string> = {
  Vibe:             'ri-sparkling-2-line',
  Light:            'ri-sun-line',
  Background:       'ri-landscape-line',
  Framing:          'ri-camera-lens-line',
  'Brand presence': 'ri-shield-star-line',
  'Show logo':      'ri-vip-crown-line',
  // Remix 4.5.0 ships `ri-text-snippet` WITHOUT a -line suffix variant
  // (some icons in 4.x don't have separate line/fill versions). Using
  // ri-text-snippet-line silently renders nothing — bit us once already.
  'Text density':   'ri-text-snippet',
};

export function CreativeDirectionFilters({
  creativeDirection,
  onCreativeDirectionChange,
  showTextDensity = false,
  withIcons = false,
  framingValues,
}: CreativeDirectionFiltersProps) {
  // Helper — returns the icon class for a given filter label only when
  // withIcons is set. Keeps the JSX below readable instead of inlining
  // `withIcons ? 'ri-...' : undefined` on every FilterRow.
  const iconFor = (label: string): string | undefined =>
    withIcons ? _FILTER_ICONS[label] : undefined;
  // Resolved framing list — caller's override OR default Product Shot
  // framings. Surprise me re-rolls within whichever list is active.
  const activeFramings = framingValues ?? FRAMING_VALUES;
  const setField = useCallback(<K extends keyof CreativeDirection>(
    key: K,
    value: CreativeDirection[K],
  ) => {
    onCreativeDirectionChange({ ...creativeDirection, [key]: value });
  }, [creativeDirection, onCreativeDirectionChange]);

  const surpriseMe = useCallback(() => {
    // Re-rolls all aesthetic values (not logo). $0 API cost.
    const pick = <T,>(arr: readonly T[]): T => arr[Math.floor(Math.random() * arr.length)];
    onCreativeDirectionChange({
      ...creativeDirection,
      mood: pick(VIBE_VALUES),
      light: pick(LIGHT_VALUES),
      background: pick(BACKGROUND_VALUES),
      framing: pick(activeFramings),
      brand_presence: pick(BRAND_PRESENCE_VALUES),
    });
  }, [creativeDirection, onCreativeDirectionChange, activeFramings]);

  return (
    <>
      <FilterZone
        title="Vibe"
        action={
          <button
            type="button"
            onClick={surpriseMe}
            className="
              inline-flex items-center gap-1 px-2.5 py-1 rounded-full
              bg-white border border-surface-dark/40 text-dark-muted
              hover:border-primary/40 hover:text-primary
              text-xs font-bold transition-colors cursor-pointer
            "
            title="Re-roll the aesthetic values — costs no credits"
          >
            <i className="ri-shuffle-line" />
            Surprise me
          </button>
        }
      >
        <FilterRow
          label="Vibe"
          values={VIBE_VALUES}
          selected={creativeDirection.mood}
          onSelect={(v) => setField('mood', v)}
          variant="image"
          icon={iconFor('Vibe')}
        />
        <FilterRow
          label="Light"
          values={LIGHT_VALUES}
          selected={creativeDirection.light}
          onSelect={(v) => setField('light', v)}
          variant="image"
          icon={iconFor('Light')}
        />
        <FilterRow
          label="Background"
          values={BACKGROUND_VALUES}
          selected={creativeDirection.background}
          onSelect={(v) => setField('background', v)}
          variant="image"
          icon={iconFor('Background')}
        />
        <FilterRow
          label="Framing"
          values={activeFramings}
          selected={creativeDirection.framing}
          onSelect={(v) => setField('framing', v)}
          variant="image"
          icon={iconFor('Framing')}
        />
      </FilterZone>

      <FilterZone title="Brand">
        <FilterRow
          label="Brand presence"
          values={BRAND_PRESENCE_VALUES}
          selected={creativeDirection.brand_presence}
          onSelect={(v) => setField('brand_presence', v)}
          icon={iconFor('Brand presence')}
        />
        <FilterRow
          label="Show logo"
          values={['On', 'Off'] as const}
          selected={creativeDirection.logo}
          onSelect={(v) => setField('logo', v)}
          icon={iconFor('Show logo')}
        />
      </FilterZone>

      {/* Text density — Product Shot only (gated by parent). Rendered as
          its own zone so the IA reads "render control" distinct from the
          aesthetic Vibe zone and the data-driven Brand zone. */}
      {showTextDensity && (
        <FilterZone title="Text">
          <FilterRow
            label="Text density"
            values={TEXT_DENSITY_VALUES}
            selected={creativeDirection.text_density ?? 'Text + CTA'}
            onSelect={(v) => setField('text_density', v)}
            icon={iconFor('Text density')}
          />
        </FilterZone>
      )}
    </>
  );
}


export default function AiGeneratePanel({
  route,
  productFile,
  productName,
  productDescription,
  aspectRatio,
  quality,
  creativeDirection,
  onCreativeDirectionChange,
  onGenerateStart,
  onResult,
  onError,
}: AiGeneratePanelProps) {
  const navigate = useNavigate();
  const { balance, loading: creditsLoading } = useCredits();
  const [generating, setGenerating] = useState(false);

  // Credit cost mirrors the backend's calculate_cost(quality, frames):
  //   Low=1, Medium=2, High=4 per frame; carousel = ×4 frames.
  const QUALITY_MULTIPLIER: Record<Quality, number> = { low: 1, medium: 2, high: 4 };
  const frameCount = route === 'carousel' ? 4 : 1;
  const creditCost = QUALITY_MULTIPLIER[quality] * frameCount;
  const insufficient = balance !== null && balance < creditCost;

  const canGenerate = !!productFile && !generating && !creditsLoading && !insufficient;

  // ---------- Generate ----------

  const handleGenerate = useCallback(async () => {
    if (!productFile) return;
    onGenerateStart();
    setGenerating(true);
    try {
      const result = await wizardAiGenerate({
        product: productFile,
        route,
        aspectRatio,
        quality,
        productName,
        productDescription,
        creativeDirection,
      });
      onResult(result);
    } catch (err) {
      if (err instanceof InsufficientCreditsError) {
        onError(err.message || 'Not enough credits. Upgrade or top up to continue.');
      } else {
        onError(err instanceof Error ? err.message : 'Something went wrong. Try Generate again.');
      }
    } finally {
      setGenerating(false);
    }
  }, [
    productFile, route, aspectRatio, quality, productName, productDescription,
    creativeDirection, onGenerateStart, onResult, onError,
  ]);

  return (
    <div className="space-y-4">
      {/* Brand-context callout — keep prominent. The one piece of info
          the user benefits from seeing: My Brand is the source of truth
          for tone/colors/logo. */}
      <div className="rounded-xl bg-accent/5 border border-accent/15 p-4 flex items-start gap-2">
        <i className="ri-information-line text-accent text-base mt-0.5" />
        <p className="text-xs text-dark-muted leading-relaxed">
          <span className="font-bold text-dark">AI uses your brand context</span> — name, brief,
          colors, and logo from <a href="/brand" className="text-primary font-bold underline">My Brand</a> — to
          stay on-tone. Update those to change the look without touching this step.
        </p>
      </div>

      {/* ================= CREATIVE DIRECTION FILTERS ================= */}
      <CreativeDirectionFilters
        creativeDirection={creativeDirection}
        onCreativeDirectionChange={onCreativeDirectionChange}
      />

      {/* ================= GENERATE ================= */}
      <div className="pt-2 space-y-1.5">
        {insufficient ? (
          <button
            type="button"
            onClick={() => navigate('/checkout')}
            disabled={generating}
            className="w-full sm:w-auto inline-flex items-center justify-center gap-1.5 px-6 py-3 rounded-full bg-amber-500 hover:bg-amber-600 text-white text-sm font-bold shadow-md shadow-amber-500/25 transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <i className="ri-lock-2-line" /> Upgrade — {creditCost} credit{creditCost === 1 ? '' : 's'} needed
          </button>
        ) : (
          <button
            type="button"
            onClick={handleGenerate}
            disabled={!canGenerate}
            className="w-full sm:w-auto inline-flex items-center justify-center gap-1.5 px-6 py-3 rounded-full bg-primary hover:bg-primary-hover text-white text-sm font-bold shadow-md shadow-primary/25 transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {generating ? (
              <><i className="ri-loader-4-line animate-spin" /> Generating…</>
            ) : (
              <>
                <i className="ri-sparkling-2-line" /> Let AI design this {route === 'carousel' ? 'carousel' : 'ad'}
                <span className="ml-1 px-2 py-0.5 rounded-full bg-white/20 text-white text-xs font-bold">
                  {creditCost} credit{creditCost === 1 ? '' : 's'}
                </span>
              </>
            )}
          </button>
        )}
        <p className="text-xs text-dark-muted">
          {insufficient
            ? `You have ${balance ?? 0} credits — need ${creditCost} to generate.`
            : route === 'carousel'
              ? '4-frame swipeable carousel (hook → benefit → proof → CTA). ~60-80s.'
              : 'Single AI-designed ad with baked-in headline, subhead, and CTA button. ~15-20s.'}
        </p>
      </div>
    </div>
  );
}


// =============================================================================
// FilterZone — visual container for a group of related FilterRows.
// Two-zone layout (Vibe / Brand) reads as "creative brief" rather than
// the competitor's flat camera-control panel.
// =============================================================================

interface FilterZoneProps {
  title: string;
  /** Optional top-right action — e.g. the Vibe zone's "Surprise me". */
  action?: React.ReactNode;
  children: React.ReactNode;
}

function FilterZone({ title, action, children }: FilterZoneProps) {
  return (
    <div className="rounded-xl border border-surface-dark/25 bg-white p-4 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-bold text-dark uppercase tracking-wide">{title}</p>
        {action}
      </div>
      <div className="space-y-2.5">{children}</div>
    </div>
  );
}


// =============================================================================
// FilterRow — label + horizontal pill toggle group. Exclusive selection
// (single value at a time). Wraps on narrow viewports.
// =============================================================================

interface FilterRowProps<T extends string> {
  label: string;
  values: readonly T[];
  selected: T;
  onSelect: (value: T) => void;
  /** 'image' renders each value as a thumbnail card with the label
   *  overlaid on a gradient — visual filters (Vibe / Light / Background /
   *  Framing). 'text' renders the original pill toggles — used for the
   *  on/off-style filters (Brand presence / Logo / Text density) where
   *  an image preview isn't meaningful. Default 'text' so existing
   *  rows keep their behavior. */
  variant?: 'image' | 'text';
  /** Optional Remix Icon class rendered next to the label (e.g.
   *  'ri-sun-line'). Threaded from CreativeDirectionFilters when its
   *  withIcons prop is set — the /appv3 prototype passes this so each
   *  row gets a small primary-tinted icon matching the AspectRow /
   *  QualityRow rows beneath it. Omit to keep the legacy text-only
   *  label (default for /appv2). */
  icon?: string;
}

function FilterRow<T extends string>({
  label,
  values,
  selected,
  onSelect,
  variant = 'text',
  icon,
}: FilterRowProps<T>) {
  return (
    <div className="flex items-start gap-3 flex-wrap">
      <span className={`
        flex-shrink-0 w-32 text-xs font-semibold text-dark-muted
        uppercase tracking-wide
        ${variant === 'image' ? 'pt-2' : 'pt-1.5'}
        ${icon ? 'flex items-center gap-1.5' : ''}
      `}>
        {icon && <i className={`${icon} text-primary text-base`} />}
        {label}
      </span>
      <div className={`
        flex flex-wrap flex-1 min-w-0
        ${variant === 'image' ? 'gap-2' : 'gap-1.5'}
      `}>
        {values.map((value) => {
          const isActive = value === selected;
          if (variant === 'image') {
            return (
              <FilterImageCard
                key={value}
                label={value}
                imageSrc={previewPathFor(value)}
                isActive={isActive}
                onClick={() => onSelect(value)}
              />
            );
          }
          return (
            <button
              key={value}
              type="button"
              onClick={() => onSelect(value)}
              className={`
                px-3 py-1.5 rounded-full text-xs font-semibold transition-colors
                cursor-pointer whitespace-nowrap
                ${isActive
                  ? 'bg-primary text-white shadow-sm shadow-primary/20'
                  : 'bg-surface border border-surface-dark/30 text-dark-muted hover:border-primary/30 hover:text-dark'}
              `}
            >
              {value}
            </button>
          );
        })}
      </div>
    </div>
  );
}


// =============================================================================
// FilterImageCard — visual filter chip. Square card with the sample
// image as the background and the label overlaid on a gradient at the
// bottom. Selected state: primary border + slight lift + corner check.
// Graceful fallback: when the sample image is missing (404 / not
// authored yet), the card shows the label centered on a neutral
// background so the row is still usable before all 18 samples land.
// =============================================================================

interface FilterImageCardProps {
  label: string;
  imageSrc: string | null;
  isActive: boolean;
  onClick: () => void;
}

function FilterImageCard({ label, imageSrc, isActive, onClick }: FilterImageCardProps) {
  const [imgFailed, setImgFailed] = useState(false);
  // Reset failed state when imageSrc changes (e.g. config update).
  useEffect(() => { setImgFailed(false); }, [imageSrc]);
  const showImage = !!imageSrc && !imgFailed;

  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      className={`
        group relative w-[88px] h-[88px] flex-shrink-0
        rounded-xl overflow-hidden cursor-pointer
        border-2 transition-all
        ${isActive
          ? 'border-primary shadow-md shadow-primary/25 -translate-y-0.5'
          : 'border-transparent hover:border-primary/30 hover:shadow-md hover:shadow-primary/10'}
      `}
    >
      {showImage ? (
        <img
          src={imageSrc!}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
          onError={() => setImgFailed(true)}
          loading="lazy"
          draggable={false}
        />
      ) : (
        // Neutral fallback — soft surface bg, label centered. Same
        // visual weight as the image variant so the row doesn't shift
        // when an image lands.
        <div className="absolute inset-0 bg-surface" />
      )}

      {/* Gradient overlay — only when image is present (the fallback
          already has good text contrast on its own). */}
      {showImage && (
        <div
          aria-hidden="true"
          className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-dark/80 via-dark/50 to-transparent"
        />
      )}

      {/* Label — overlays on image OR centered on fallback. text-[11px]
          balances readability against tight card width. line-clamp-2
          for two-word + three-word values like "Golden hour" and
          "Warm & lifestyle". */}
      <span
        className={`
          absolute inset-x-1.5 z-10 text-[11px] font-bold leading-tight
          line-clamp-2 text-center
          ${showImage
            ? 'bottom-1.5 text-white drop-shadow-sm'
            : 'top-1/2 -translate-y-1/2 text-dark'}
        `}
      >
        {label}
      </span>

      {/* Active checkmark — top-right corner. Only when image is
          present (the fallback's primary border already signals
          selection clearly). */}
      {isActive && showImage && (
        <div className="absolute top-1.5 right-1.5 z-10 w-5 h-5 rounded-full bg-primary flex items-center justify-center shadow-sm">
          <i className="ri-check-line text-white text-xs" />
        </div>
      )}
    </button>
  );
}
